// SUPER-ADMIN benchmark console.
//
// Starts runs, shows history, and drills into every scored cell — including the
// artifact the worker produced, the perturbed input, each failed check, and the
// judge's per-criterion reasoning. All of that is admin-only; the public page
// gets a scores-only projection built server-side in evalController.
//
// Runs are asynchronous: POST returns 202 immediately and this page polls until
// the run leaves 'running'.
const React = require('react');
const { createElement: h, useState, useEffect, useRef } = React;
const benchmarkStore = require('../lib/benchmarkStore');
require('./AdminEvalPage.css');

var POLL_MS = 5000;

// "All workers" runs the focus set (askcode + codegen + copilot). Planner and
// Doctor Chat are registered but outside that set — pick them explicitly.
//
// Note: "Workflow Doctor Chat" is a DIFFERENT service from "Workflow Copilot"
// (/workflow-doctor-chat vs /workflow-copilot) even though Doctor Chat is
// marketed under the Copilot name. Labels here name the service, not the brand,
// so a run result is never ambiguous about what was measured.
var WORKERS = [
  { id: '', label: 'All workers (AskCode + Codegen + Copilot)' },
  { id: 'askcode', label: 'AskCode (node repair)' },
  { id: 'codegen', label: 'Node Code Generation' },
  { id: 'copilot', label: 'Workflow Copilot (canvas edits)' },
  { id: 'planner', label: 'Workflow Planner (slow)' },
  { id: 'doctor', label: 'Workflow Doctor Chat (separate service)' }
];

var ATTACKS = [
  { id: 'char-swap', label: 'Character swap (typos)' },
  { id: 'char-drop', label: 'Character deletion' },
  { id: 'word-sub', label: 'Informal word substitution' },
  { id: 'distractor', label: 'Irrelevant sentence' },
  { id: 'ramble', label: 'Hedged rambling' },
  { id: 'injection', label: 'Prompt injection' }
];

var PROMPTS = [
  { id: 'terse', label: 'Task-oriented / terse' },
  { id: 'role', label: 'Role-oriented' },
  { id: 'cot', label: 'Chain-of-thought' },
  { id: 'vague', label: 'Under-specified' }
];

function band(score) {
  if (score >= 85) return 'strong';
  if (score >= 70) return 'good';
  if (score >= 50) return 'fair';
  return 'weak';
}

// ── run configuration ───────────────────────────────────────────────────────

function CheckList(props) {
  return h('div', { className: 'ae-checks' },
    props.options.map(function (o) {
      var on = props.value.indexOf(o.id) !== -1;
      return h('label', { className: 'ae-check' + (on ? ' ae-check--on' : ''), key: o.id },
        h('input', {
          type: 'checkbox',
          checked: on,
          onChange: function () {
            props.onChange(on
              ? props.value.filter(function (v) { return v !== o.id; })
              : props.value.concat([o.id]));
          }
        }),
        o.label
      );
    })
  );
}

function RunConfig(props) {
  var [worker, setWorker] = useState('');
  var [attacks, setAttacks] = useState([]);
  var [prompts, setPrompts] = useState([]);
  var [full, setFull] = useState(false);

  // Cost scales with the matrix, so show the cell count before spending money.
  var axisCells = full
    ? (prompts.length + 1) * (attacks.length + 1)
    : 1 + prompts.length + attacks.length;

  return h('div', { className: 'ae-config' },
    h('div', { className: 'ae-config-row' },
      h('label', { className: 'ae-label' }, 'Workers'),
      h('select', {
        className: 'ae-select', value: worker,
        onChange: function (e) { setWorker(e.target.value); }
      }, WORKERS.map(function (w) { return h('option', { value: w.id, key: w.id }, w.label); }))
    ),
    h('div', { className: 'ae-config-row' },
      h('label', { className: 'ae-label' }, 'Prompt variants'),
      h(CheckList, { options: PROMPTS, value: prompts, onChange: setPrompts })
    ),
    h('div', { className: 'ae-config-row' },
      h('label', { className: 'ae-label' }, 'Attacks'),
      h(CheckList, { options: ATTACKS, value: attacks, onChange: setAttacks })
    ),
    h('div', { className: 'ae-config-row' },
      h('label', { className: 'ae-label' }, 'Matrix'),
      h('label', { className: 'ae-check' + (full ? ' ae-check--on' : '') },
        h('input', { type: 'checkbox', checked: full, onChange: function () { setFull(!full); } }),
        'Full grid (every prompt × every attack)'
      )
    ),
    h('div', { className: 'ae-config-foot' },
      h('span', { className: 'ae-cost' },
        axisCells + ' cell' + (axisCells === 1 ? '' : 's') + ' per scenario · real LLM spend'
      ),
      h('button', {
        className: 'ae-btn ae-btn--primary',
        disabled: props.busy,
        onClick: function () {
          props.onStart({
            worker: worker || null,
            prompts: prompts.length ? ['plain'].concat(prompts) : null,
            attacks: attacks.length ? ['clean'].concat(attacks) : null,
            full: full
          });
        }
      }, props.busy ? 'Running…' : 'Start benchmark run')
    )
  );
}

// ── case detail ─────────────────────────────────────────────────────────────

function CaseDetail(props) {
  var c = props.case_;
  if (c.skipped) {
    return h('div', { className: 'ae-detail' },
      h('div', { className: 'ae-skip' }, 'Skipped — ' + c.skipReason)
    );
  }
  return h('div', { className: 'ae-detail' },
    c.perturbedInput ? h('div', { className: 'ae-detail-block' },
      h('div', { className: 'ae-detail-title' }, 'Input the worker received'),
      h('pre', { className: 'ae-pre ae-pre--input' }, c.perturbedInput)
    ) : null,

    h('div', { className: 'ae-detail-block' },
      h('div', { className: 'ae-detail-title' }, 'Deterministic checks'),
      h('div', { className: 'ae-rows' },
        (c.checks || []).map(function (chk) {
          return h('div', { className: 'ae-row', key: chk.id },
            h('span', { className: 'ae-verdict ae-verdict--' + (chk.pass ? 'pass' : 'fail') }, chk.pass ? 'PASS' : 'FAIL'),
            h('span', { className: 'ae-row-label' }, chk.label),
            h('span', { className: 'ae-weight' }, '×' + chk.weight),
            chk.detail ? h('div', { className: 'ae-row-detail' }, chk.detail) : null
          );
        })
      )
    ),

    h('div', { className: 'ae-detail-block' },
      h('div', { className: 'ae-detail-title' }, 'Judge criteria'),
      h('div', { className: 'ae-rows' },
        (c.judged || []).map(function (j) {
          return h('div', { className: 'ae-row', key: j.id },
            h('span', { className: 'ae-jscore ae-jscore--' + (j.score >= 4 ? 'hi' : j.score >= 3 ? 'mid' : 'lo') }, j.score + '/5'),
            h('span', { className: 'ae-row-label' }, j.label),
            h('span', { className: 'ae-weight' }, '×' + j.weight),
            j.why ? h('div', { className: 'ae-row-detail' }, j.why) : null
          );
        })
      )
    ),

    h('div', { className: 'ae-detail-block' },
      h('div', { className: 'ae-detail-title' },
        'Artifact',
        h('span', { className: 'ae-detail-meta' },
          Math.round(c.durationMs / 100) / 10 + 's · ' +
          (c.tokens ? (c.tokens.inputTokens + ' in / ' + c.tokens.outputTokens + ' out tokens') : '')
        )
      ),
      h('pre', { className: 'ae-pre' }, c.artifactText || '(none)')
    )
  );
}

function CaseRow(props) {
  var c = props.case_;
  var [open, setOpen] = useState(false);
  var tags = [];
  if (c.promptVariant && c.promptVariant !== 'plain') tags.push('prompt:' + c.promptVariant);
  if (c.attack && c.attack !== 'clean') tags.push('attack:' + c.attack);

  return h('div', { className: 'ae-case' },
    h('div', {
      className: 'ae-case-head' + (open ? ' ae-case-head--open' : ''),
      onClick: function () { setOpen(!open); }
    },
      h('span', { className: 'ae-case-caret' }, open ? '▾' : '▸'),
      h('span', {
        className: 'ae-case-score ae-case-score--' + (c.skipped ? 'skip' : band(c.composite))
      }, c.skipped ? '—' : c.composite),
      h('span', { className: 'ae-case-name' }, c.scenarioLabel),
      tags.map(function (t) { return h('span', { className: 'ae-tag', key: t }, t); }),
      !c.skipped && !c.hardPass ? h('span', { className: 'ae-tag ae-tag--fail' }, 'hard check failed') : null
    ),
    open ? h(CaseDetail, { case_: c }) : null
  );
}

// ── run view ────────────────────────────────────────────────────────────────

function RunView(props) {
  var run = props.run;
  if (!run) return null;

  if (run.status === 'running') {
    return h('div', { className: 'ae-running' },
      h('div', { className: 'ae-spinner' }),
      'Benchmark in progress… this page refreshes automatically.'
    );
  }
  if (run.status === 'failed') {
    return h('div', { className: 'ae-error' }, 'Run failed: ' + (run.error || 'unknown error'));
  }

  return h('div', null,
    h('div', { className: 'ae-summary' },
      h('div', { className: 'ae-overall' },
        h('div', { className: 'ae-overall-score ae-overall-score--' + band(run.overallScore) }, run.overallScore),
        h('div', { className: 'ae-overall-label' }, 'Overall (baseline cells)')
      ),
      h('div', { className: 'ae-workers' },
        (run.workers || []).map(function (w) {
          return h('div', { className: 'ae-worker', key: w.workerId },
            h('div', { className: 'ae-worker-score ae-worker-score--' + band(w.score) }, w.score),
            h('div', { className: 'ae-worker-label' }, w.label),
            h('div', { className: 'ae-worker-sub' }, w.tier + ' · hard checks ' + w.hardPassRate + '%')
          );
        })
      )
    ),

    (run.robustness && run.robustness.length) ? h('div', { className: 'ae-block' },
      h('h3', { className: 'ae-h3' }, 'Robustness (PDR vs matched clean baseline)'),
      h('div', { className: 'ae-rows' },
        run.robustness.map(function (r) {
          return h('div', { className: 'ae-row', key: r.attack },
            h('span', { className: 'ae-pdr ae-pdr--' + (r.pdr >= 30 ? 'weak' : r.pdr >= 15 ? 'fair' : 'good') }, r.pdr + '%'),
            h('span', { className: 'ae-row-label' }, r.label + '  (' + r.level + ')'),
            h('span', { className: 'ae-weight' }, 'score ' + r.score)
          );
        })
      )
    ) : null,

    (run.promptSensitivity && run.promptSensitivity.length > 1) ? h('div', { className: 'ae-block' },
      h('h3', { className: 'ae-h3' }, 'Prompt sensitivity'),
      h('div', { className: 'ae-rows' },
        run.promptSensitivity.map(function (p) {
          return h('div', { className: 'ae-row', key: p.variant },
            h('span', { className: 'ae-case-score ae-case-score--' + band(p.score) }, p.score),
            h('span', { className: 'ae-row-label' }, p.label)
          );
        })
      )
    ) : null,

    h('div', { className: 'ae-block' },
      h('h3', { className: 'ae-h3' }, 'Cases'),
      (run.cases || []).map(function (c, i) {
        return h(CaseRow, { case_: c, key: c.workerId + ':' + c.scenarioId + ':' + c.promptVariant + ':' + c.attack + ':' + i });
      })
    ),

    run.totals ? h('div', { className: 'ae-totals' },
      run.totals.cases + ' cases scored · ' +
      (run.totals.skippedCases ? (run.totals.skippedCases + ' skipped · ') : '') +
      Math.round((run.totals.durationMs || 0) / 1000) + 's · ' +
      run.totals.inputTokens + ' in / ' + run.totals.outputTokens + ' out tokens'
    ) : null
  );
}

// ── page ────────────────────────────────────────────────────────────────────

function AdminEvalPage() {
  var [runs, setRuns] = useState([]);
  var [selected, setSelected] = useState(null);
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState('');
  var pollRef = useRef(null);

  function loadRuns() {
    return benchmarkStore.adminListRuns()
      .then(function (list) {
        setRuns(list);
        return list;
      })
      .catch(function (err) { setError(err.message || 'Could not load runs.'); return []; });
  }

  function openRun(id) {
    return benchmarkStore.adminGetRun(id)
      .then(function (r) { setSelected(r); return r; })
      .catch(function (err) { setError(err.message || 'Could not load run.'); return null; });
  }

  useEffect(function () {
    loadRuns().then(function (list) {
      if (list.length) openRun(list[0].id);
    });
    return function () { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Poll only while a run is actually in progress, then stop.
  useEffect(function () {
    if (!selected || selected.status !== 'running') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(function () {
      openRun(selected.id).then(function (r) {
        if (r && r.status !== 'running') { loadRuns(); setBusy(false); }
      });
    }, POLL_MS);
    return function () { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selected && selected.id, selected && selected.status]);

  function start(options) {
    setBusy(true);
    setError('');
    benchmarkStore.adminStartRun(options)
      .then(function (r) { return loadRuns().then(function () { return openRun(r.id); }); })
      .catch(function (err) {
        setBusy(false);
        setError(err.message || 'Could not start the run.');
      });
  }

  return h('div', { className: 'ae-page' },
    h('div', { className: 'ae-head' },
      h('h1', { className: 'ae-h1' }, 'AI worker benchmark'),
      h('p', { className: 'ae-sub' },
        'Runs cost real money and take minutes. Results published here appear on the public ',
        'benchmark page as scores only — no prompts, code, or costs are exposed publicly.'
      )
    ),

    error ? h('div', { className: 'ae-error' }, error) : null,
    h(RunConfig, { onStart: start, busy: busy }),

    h('div', { className: 'ae-body' },
      h('div', { className: 'ae-history' },
        h('div', { className: 'ae-history-title' }, 'History'),
        runs.map(function (r) {
          return h('div', {
            className: 'ae-history-item' + (selected && selected.id === r.id ? ' ae-history-item--on' : ''),
            key: r.id,
            onClick: function () { openRun(r.id); }
          },
            h('span', { className: 'ae-history-score' }, r.status === 'complete' ? r.overallScore : '—'),
            h('span', { className: 'ae-history-meta' },
              h('div', null, new Date(r.startedAt).toLocaleString()),
              h('div', { className: 'ae-history-status ae-history-status--' + r.status }, r.status)
            )
          );
        })
      ),
      h('div', { className: 'ae-main' }, h(RunView, { run: selected }))
    )
  );
}

module.exports = AdminEvalPage;
